import { useState, type CSSProperties } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ContractStatus,
  DeploymentReason,
  PricingScheme,
  UserRole,
  resolveContractLineAmount,
  type ContractSummaryDto,
} from '@bhops/shared';
import { contractsApi } from '@/api/contracts';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { MaskedPrice } from '@/components/MaskedPrice';
import { useAuthStore } from '@/stores/auth';
import { ContractTerminateModal } from './ContractTerminateModal';
import { ContractEditModal } from './ContractEditModal';
import { InfrastructureOrderModal } from '../infrastructure/InfrastructureOrderModal';
import { kstYmd } from '@/lib/format-date';
// 정정9(26-07-29) — 오너 지시("구분은 구독과 판매로만 표시되면 됩니다")에 따라 줄 표의
// '이유' 칸도 두 값으로 접는다. 접기 정본은 lib/contract-reason 한 곳뿐이다.
import { foldContractReasonToKind, formatContractReason } from '@/lib/contract-reason';
import { amountOrNone } from '@/lib/amount-or-none';
import { canSeePrice } from '@/permissions';

function usePdfDownload(id: string) {
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function downloadPdf() {
    setDownloading(true);
    setPdfError(null);
    try {
      await contractsApi.downloadPdf(id);
    } catch (e) {
      setPdfError(extractApiError(e));
    } finally {
      setDownloading(false);
    }
  }

  return { downloading, pdfError, downloadPdf };
}

const STATUS_LABEL: Record<ContractStatus, string> = {
  DRAFT: '초안',
  ACTIVE: '활성',
  EXPIRED: '만료',
  TERMINATED: '해지',
};

const STATUS_COLOR: Record<ContractStatus, string> = {
  DRAFT: 'var(--fg-muted)',
  ACTIVE: 'var(--robot-operating)',
  EXPIRED: 'var(--fg-warning)',
  TERMINATED: 'var(--fg-danger)',
};

const SCHEME_LABEL: Record<PricingScheme, string> = {
  MONTHLY: '월 단위',
  ONE_TIME: '일시불',
  PARTIAL: '보증금과 월 납부',
};

// 계약 사실화 1단계 정정8(26-07-29) — '0 은 없음' 판정은 이 화면의 지역 함수였다
// (정정7). 그동안 **같은 값을 읽는 고객사 상세·고객사 목록은 그대로 ₩0 을 찍어**, 같은
// 계약을 두고 세 화면이 다른 말을 했다. 판정을 lib/amount-or-none 로 승격해 한 벌로
// 모았고(근거·마스킹 순서 주의는 그 파일 주석), 이 화면은 그것을 부른다.
//
// ⚠ 접은 결과를 MaskedPrice 에 넘긴다 — 판정이 마스킹보다 뒤에 있어야 가격 권한이
// 없는 사용자에게 0 여부가 새로 노출되지 않는다.

// Server detail include = lines + pricings + customer + store +
// deal back-ref. Widen the wire DTO to capture them.
type ContractDetail = ContractSummaryDto & {
  renewalScheduledAt?: string | null;
  // §E-3-4 (PDF p.19) — field team needs contact-relevant fields
  // exposed on the contract detail. legalName + segment + salesOwnerCode
  // give enough hooks to look up contact info elsewhere; phone/email
  // live on a future Customer.contacts collection (Phase 6).
  customer?: {
    id: string;
    displayName: string;
    legalName?: string;
    segment?: string;
    salesOwnerCode?: string | null;
  };
  store?: { id: string; name: string; branchName: string | null } | null;
  deal?: { id: string; humanId: string; stage: string };
  lines?: Array<{
    id: string;
    quantity: number;
    reason: string;
    unitPriceMonthly: string;
    // 계약 사실화 1단계 정정7(26-07-29) — 판매 줄의 금액이 사는 칸. 서버는 계약 상세에
    // 이 값을 이미 내려주고 있었는데(lines include = 전 스칼라) 화면이 읽지 않아,
    // 판매 계약을 열면 금액이 전부 0/빈칸으로 보였다. BigInt → 문자열(전역 직렬화).
    listPriceKrw?: string | null;
    currency: string;
    effectiveFrom: string;
    notes: string | null;
    model?: { code: string; displayName: string };
  }>;
  pricings?: Array<{
    id: string;
    scheme: PricingScheme;
    totalMonthly: string | null;
    totalOneTime: string | null;
    deposit: string | null;
    currency: string;
    exchangeRateSnapshot: string;
    effectiveFrom: string;
    paymentSchedule: string | null;
  }>;
};

export function ContractDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const userRole = useAuthStore((s) => s.user?.role);
  const canTerminate = userRole === UserRole.ADMIN;
  const canEdit = userRole === UserRole.ADMIN || userRole === UserRole.MANAGER;
  // 계약 사실화 1단계 정정9(26-07-29) §D-3 — 이 화면에서 **금액에 관한 판정을 컴포넌트
  // 밖에서 하는 자리**는 가격 권한으로 먼저 감싸야 한다. MaskedPrice 는 값을 가려 주지만
  // "그런 금액이 있다/없다" 는 컴포넌트 밖에서 새기 때문이다(아래 판매 대금 카드).
  // 이 판정은 서버 마스킹으로 대체할 수 없다 — listPriceKrw 는 price-mask.interceptor 의
  // PRICE_FIELDS 에 들어 있지 않고(그 파일 주석이 이름 충돌 때문에 넣지 말라고 못박고
  // 있다), `/contracts/:id` 에는 역할 제한이 없다. 그래서 화면이 막는다.
  const priceVisible = userRole ? canSeePrice(userRole) : false;

  const [terminateOpen, setTerminateOpen] = useState(false);
  // round7 §1-1 — 계약 퍼지(ADMIN).
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const purgeMx = useMutation({
    mutationFn: (confirmHumanId: string) => contractsApi.purge(id, confirmHumanId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['infrastructure-order-by-contract'] });
      toast.success('계약을 완전 삭제했습니다.', '계약 완전 삭제');
      navigate('/contracts', { replace: true });
    },
    onError: (err) => toast.error(extractApiError(err), '완전 삭제 실패'),
  });
  const [editOpen, setEditOpen] = useState(false);
  const { downloading, pdfError, downloadPdf } = usePdfDownload(id);
  // §E-3 — infrastructure order entry. Lazy-load the existing row only
  // when the contract is ACTIVE / EXPIRED (DRAFT contracts can't have
  // an infra order yet — sales hasn't activated them).
  const [infraOpen, setInfraOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', id],
    queryFn: () => contractsApi.get(id) as Promise<ContractDetail>,
    enabled: id !== '',
  });

  // §E-3 — fetch the existing InfrastructureOrder for this contract
  // (or null). Used both to label the entry button (편집 vs 새 발주)
  // and to seed the modal with current values.
  const infraQ = useQuery({
    queryKey: ['infrastructure-order-by-contract', id],
    queryFn: () => infrastructureOrdersApi.byContract(id),
    enabled: id !== '' && data?.status !== ContractStatus.DRAFT,
  });

  if (isLoading) {
    return (
      <div style={empty} className="kuun-caption">
        불러오는 중…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={empty}>
        <span style={{ color: 'var(--fg-danger)' }}>{error ? extractApiError(error) : '없음'}</span>
      </div>
    );
  }

  const canBeTerminated =
    canTerminate &&
    (data.status === ContractStatus.ACTIVE || data.status === ContractStatus.EXPIRED);

  // BR-CNT-06: only DRAFT contracts are mutable. ACTIVE+ require
  // terminate-and-replace.
  const canBeEdited = canEdit && data.status === ContractStatus.DRAFT;

  // 계약 사실화 1단계 정정7(26-07-29) — 판매 대금 합계. 가격 책정 카드가 월 금액
  // (ContractPricing.totalMonthly) 한 가지만 찍었으므로, 판매 계약을 열면 카드 전체가
  // 비거나 "월: 0 KRW" 였다. 판매 줄의 대금은 계약 줄에만 있으니 **줄에서 낸다.**
  //
  // 셈법은 사업 대시보드와 같다 — 판매 줄(reason=SALE)의 `판매가 × 대수` 합
  // (apps/api team-dashboard.service getBusinessRevenue: "SALE → listPriceKrw × quantity").
  // 두 화면이 다른 셈법을 쓰면 오너가 대시보드와 계약서를 대조할 때 어긋난다.
  //
  // 렌탈 줄의 판매가(고객사 화면의 '출주도료')는 **합에 넣지 않는다** — 그것은 참고용
  // 정가이고 이 계약의 대금이 아니다(대시보드도 렌탈 줄은 월 금액만 센다).
  // 통화가 섞인 계약은 합을 만들지 않는다(줄별 금액은 아래 표에 그대로 보인다).
  //
  // ⚠ 합에는 **정수 문자열로 온 값만** 넣는다 — 아래 BigInt() 는 숫자가 아닌 값에 예외를
  // 던지고, 그 예외는 렌더 중에 터져 계약 상세를 통째로 백지로 만든다. 형식이 어긋난
  // 줄은 합에서 빠지되 줄별 금액은 아래 표에 그대로 보인다(조용히 화면을 잃지 않는다).
  //
  // 계약 사실화 1단계 정정9(26-07-29) — **가격 권한이 없으면 합계를 아예 만들지 않는다.**
  // 종전에는 카드의 표시 조건과 캡션('판매가가 입력된 판매 줄 N건')이 MaskedPrice **밖에서**
  // listPriceKrw 로 판정돼, 금액을 볼 수 없는 역할(필드·제조 등)이 "이 계약에 0 이 아닌
  // 판매가가 있다" 와 그 줄 수를 읽을 수 있었다 — 이 화면들이 스스로 세운 규칙
  // (lib/amount-or-none: 마스킹이 앞, 0 접기가 뒤)을 어긴 유일한 자리였다.
  // 여기서 빈 배열로 접으면 saleTotal 이 null 이 되어 카드·캡션이 함께 사라지고, 가격
  // 책정 카드는 '가격 정보 없음' 을 그대로 띄운다 = 정정7 이전과 **같은 화면**이다.
  const saleLines = !priceVisible
    ? []
    : (data.lines ?? []).filter(
        (l) =>
          l.reason === DeploymentReason.SALE &&
          amountOrNone(l.listPriceKrw) !== null &&
          /^\d+$/.test(String(l.listPriceKrw)),
      );
  const saleCurrencies = Array.from(new Set(saleLines.map((l) => l.currency)));
  const saleTotal =
    saleLines.length > 0 && saleCurrencies.length === 1
      ? saleLines
          .reduce((sum, l) => sum + BigInt(l.listPriceKrw!) * BigInt(l.quantity), 0n)
          .toString()
      : null;

  // 계약 사실화 1단계 정정9(26-07-29) — 합계에서 빠지는 판매 줄(금액이 '월 단가' 칸에
  // 들어간 계약 등록 마법사 경로). 아래 표는 그 금액을 **판매가로** 보여 주므로(정본
  // 판정 = shared resolveContractLineAmount), 캡션이 "판매가가 입력된 판매 줄 N건" 만
  // 말하면 표에 보이는 판매가 개수와 다시 어긋난다. 빠진 줄이 있으면 캡션이 스스로 밝힌다.
  // (합계 셈법 자체는 사업 대시보드와 같게 유지한다 — 위 주석 근거.)
  const saleLinesFromMonthlyColumn = !priceVisible
    ? []
    : (data.lines ?? []).filter((l) => resolveContractLineAmount(l).fromMonthlyColumn);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Link
          to="/contracts"
          className="kuun-caption"
          style={{ color: 'var(--fg-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <Icon name="arrow-left" size={14} aria-hidden /> 계약 목록
        </Link>
        <h1
          className="kuun-h2"
          style={{ margin: '4px 0 0', display: 'flex', gap: 10, alignItems: 'baseline' }}
        >
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--fg-muted)' }}>
            {data.humanId}
          </span>
          <span>{data.customer?.displayName ?? '-'}</span>
        </h1>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill
            variant="solid"
            tint={STATUS_COLOR[data.status]}
            label={STATUS_LABEL[data.status]}
          />
          {data.terminationReason && (
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              ({data.terminationReason})
            </span>
          )}
          {data.deal && (
            <Link
              to={`/deals/${data.deal.id}`}
              className="kuun-caption"
              style={{ color: 'var(--fg-muted)', textDecoration: 'none' }}
            >
              ← {data.deal.humanId} ({data.deal.stage})
            </Link>
          )}
        </div>
      </div>

      {(canBeTerminated || canBeEdited || data.status !== ContractStatus.DRAFT) && (
        <div style={actionRow}>
          {canBeEdited && (
            <Button type="button" variant="secondary" onClick={() => setEditOpen(true)}>
              헤더 편집
            </Button>
          )}
          {/* §E-3 — 인프라 발주 진입점. ACTIVE / EXPIRED / TERMINATED
              계약은 모두 발주 이력을 볼 수 있어야 하므로 DRAFT 만 제외. */}
          {data.status !== ContractStatus.DRAFT && (
            <Button type="button" onClick={() => setInfraOpen(true)}>
              인프라 발주{infraQ.data ? ' 편집' : ''}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={downloadPdf} disabled={downloading}>
            {downloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
          </Button>
          {canBeTerminated && (
            <Button type="button" variant="danger" onClick={() => setTerminateOpen(true)}>
              계약 해지
            </Button>
          )}
          {/* round7 §1-1 — 계약 퍼지(전 상태, ADMIN): 인프라 발주·라인·할당까지
              연쇄 영구 삭제 + 배정 로봇 해제. 계약번호 타이핑으로 확인. */}
          {userRole === UserRole.ADMIN && (
            <Button
              type="button"
              variant="danger"
              disabled={purgeMx.isPending}
              onClick={() => {
                const typed = window.prompt(
                  `계약 "${data.humanId}"와 이 계약의 인프라 발주, 라인, 할당을 영구 삭제하고 배정된 로봇을 풀어 줍니다.\n` +
                    `되돌릴 수 없습니다. 확인을 위해 계약번호를 그대로 입력해 주세요.`,
                );
                if (typed === null) return;
                if (typed.trim() !== data.humanId) {
                  toast.error('계약번호가 일치하지 않습니다.', '완전 삭제 취소');
                  return;
                }
                purgeMx.mutate(typed.trim());
              }}
            >
              {purgeMx.isPending ? '삭제 중…' : '완전 삭제'}
            </Button>
          )}
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)', marginLeft: 8 }}>
            {canBeEdited
              ? '초안 상태의 계약만 관리자나 매니저가 헤더를 고칠 수 있습니다.'
              : data.status === ContractStatus.DRAFT
                ? '계약이 활성화된 뒤에 인프라 발주를 할 수 있습니다.'
                : '해지는 관리자만 할 수 있고, 사유와 메모를 적어야 합니다.'}
          </span>
          {pdfError && (
            <span className="kuun-caption" style={{ color: 'var(--fg-danger)', marginLeft: 8 }}>
              {pdfError}
            </span>
          )}
        </div>
      )}

      {/* §E-3-4 (PDF p.19) — 필드팀이 고객사 연락에 사용할 수 있는
          식별 정보. 전화/이메일은 Customer.contacts 모델 도입 시 추가
          (Phase 6). 현재는 사명·세그먼트·영업 담당 코드를 노출. */}
      {data.customer && (
        <section style={panel}>
          <header style={panelHeader}>
            고객사 정보
            {/* docs/work_log/p1-stale-links — 기존 `/business/customers` 는
                고객사 마스터 목록으로 빠지는 redirect 만 일으켜 운영자가
                현재 고객 detail 로 다시 들어가야 했음. 직접 detail 로 점프. */}
            <Link
              to={`/customers/${data.customer.id}`}
              className="kuun-caption"
              style={{ marginLeft: 8, color: 'var(--fg-muted)', textDecoration: 'none' }}
            >
              고객사 상세로 이동
            </Link>
          </header>
          <div style={detailGrid}>
            <Cell label="표시명">{data.customer.displayName}</Cell>
            {data.customer.legalName && data.customer.legalName !== data.customer.displayName && (
              <Cell label="법인명">{data.customer.legalName}</Cell>
            )}
            {data.customer.segment && <Cell label="세그먼트">{data.customer.segment}</Cell>}
            {data.customer.salesOwnerCode && (
              <Cell label="영업 담당">
                <code>{data.customer.salesOwnerCode}</code>
              </Cell>
            )}
            {data.store && (
              <Cell label="설치 매장">
                {[data.store.name, data.store.branchName].filter(Boolean).join(' · ')}
              </Cell>
            )}
          </div>
        </section>
      )}

      <section style={panel}>
        <header style={panelHeader}>계약 정보</header>
        <div style={detailGrid}>
          {/* 추가수정 2차(26-07-27) §2 — 날짜 표시를 KST 로 통일. toYmd(UTC
              자르기)는 한국 자정으로 저장된 계약일을 하루 이르게 보여줬다. */}
          <Cell label="기간">
            {kstYmd(data.startDate) ?? '-'} ~ {kstYmd(data.endDate) ?? '-'}
          </Cell>
          <Cell label="개월 수">
            <code>{data.durationMonths}</code>
          </Cell>
          {data.signedAt && <Cell label="서명일">{kstYmd(data.signedAt) ?? '-'}</Cell>}
          {data.renewalScheduledAt && (
            <Cell label="재계약 예정">{kstYmd(data.renewalScheduledAt) ?? '-'}</Cell>
          )}
          <Cell label="매장">
            {data.store
              ? [data.store.name, data.store.branchName].filter(Boolean).join(' · ')
              : '미지정'}
          </Cell>
          <Cell label="알림 (D-90 / D-30 / D-0)">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {[data.alertedD90At, data.alertedD30At, data.alertedD0At].map((d, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {i > 0 && <span style={{ color: 'var(--fg-subtle)' }}>/</span>}
                  {d ? (
                    <Icon name="check" size={14} color="var(--robot-operating)" />
                  ) : (
                    <span style={{ color: 'var(--fg-subtle)' }}>·</span>
                  )}
                </span>
              ))}
            </span>
          </Cell>
        </div>
      </section>

      <section style={panel}>
        <header style={panelHeader}>
          <span>계약 라인</span>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {data.lines?.length ?? 0}건
          </span>
        </header>
        <table style={lineTable}>
          <thead>
            <tr>
              <th style={th}>모델</th>
              <th style={th}>대수</th>
              <th style={th}>월 단가</th>
              {/* 계약 사실화 1단계 정정7(26-07-29) — 판매 줄의 금액이 들어갈 칸이 이 표에
                  없었다(월 단가만 있었다). 그래서 판매 계약을 열면 대당 0 만 보였다.
                  단어는 고객사 화면과 같은 '판매가'(그 화면 '판매가(대당)')를 쓴다. */}
              <th style={th}>판매가</th>
              <th style={th}>통화</th>
              <th style={th}>이유</th>
              <th style={th}>유효일</th>
            </tr>
          </thead>
          <tbody>
            {(data.lines ?? []).map((l) => {
              // 계약 사실화 1단계 정정9(26-07-29) — 이 줄의 금액이 **어느 칸에 있고 무슨
              // 뜻인지**는 정본 한 벌(shared resolveContractLineAmount)이 판정한다. 종전에는
              // 그 폴백이 계약서(PDF)에만 있어, 마법사로 만든 판매 줄을 두고
              //   계약서: "18,000,000 (sale)"(판매가) / 이 화면: "월 단가 18,000,000"
              // 이라고 고객 문서와 운영자 화면이 서로 반대되는 말을 했다.
              const amount = resolveContractLineAmount(l);
              // 판매 줄의 금액이 월 칸에 들어와 있으면 그 금액은 아래 판매가 칸에서
              // 보여 준다 — 같은 금액을 두 칸에 두 뜻으로 적지 않는다.
              const monthlyCell = amount.fromMonthlyColumn
                ? null
                : amountOrNone(l.unitPriceMonthly);
              const saleCell = amount.isSale ? amount.amount : amountOrNone(l.listPriceKrw);
              return (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={td}>{l.model?.displayName ?? l.model?.code ?? '-'}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{l.quantity}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                    {/* §D-3 — masked for non-sales roles.
                        계약 사실화 1단계 정정7(26-07-29) — 0(=없음)은 '-' 로. 판매 줄의
                        월 구독료 칸은 컬럼이 NOT NULL 이라 0 으로 저장된다(위 amountOrNone). */}
                    <MaskedPrice value={monthlyCell} />
                  </td>
                  {/* 계약 사실화 1단계 정정7(26-07-29) — 판매 줄의 금액. 형태에 따라 줄이
                      쓰는 칸이 갈리므로(판매=판매가 / 렌탈=월 단가) 두 칸을 나란히 두고
                      각 줄은 자기 칸만 채운다 — 혼합 계약에서도 줄마다 맞는 금액이 보인다. */}
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                    <MaskedPrice value={saleCell} />
                    {/* 정정9 — 마법사 경로. 금액이 어느 칸에 저장돼 있는지 운영자가 알아야
                        고칠 수 있다. ⚠ 가격 권한이 없는 역할에는 **보이지 않는다** — 이
                        문구 자체가 "금액이 있다" 는 신호이기 때문이다(마스킹이 앞). */}
                    {priceVisible && amount.fromMonthlyColumn && (
                      <div
                        className="kuun-caption"
                        style={{ color: 'var(--fg-muted)', fontFamily: 'var(--font-sans)' }}
                        title="계약 등록 마법사에는 판매가 칸이 없어 판매 줄의 금액이 월 단가 칸에 저장됩니다. 계약서 PDF에도 이 금액이 판매가로 인쇄됩니다."
                      >
                        월 단가 칸에 입력됨
                      </div>
                    )}
                  </td>
                  <td style={td}>{l.currency}</td>
                  {/* 계약 사실화 1단계(26-07-28) 결함 ③ — 영문 enum('SALE' /
                      'LEASE_OPERATING')이 그대로 노출되던 칸. 로봇 목록 '구분'과
                      같은 표기를 공용 포맷터(lib/contract-reason)에서 가져온다.
                      정정9(26-07-29) 오너 지시 — "구분은 구독과 판매로만 표시되면 됩니다."
                      7값(파일럿·시연·행사·사내·금융리스)을 두 값으로 접는다. 접기 정본은
                      lib/contract-reason 의 foldContractReasonToKind 한 곳뿐이다.
                      ⚠ 저장은 7값 그대로다 — 접기는 표시 전용이고, 모르는 값은 접지 않고
                      원문 표기 폴백으로 넘긴다(조용히 다른 뜻으로 뭉개지 않는다). */}
                  <td style={td}>
                    {foldContractReasonToKind(l.reason) ?? formatContractReason(l.reason)}
                  </td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                    {kstYmd(l.effectiveFrom) ?? '-'}
                  </td>
                </tr>
              );
            })}
            {(!data.lines || data.lines.length === 0) && (
              <tr>
                {/* 정정7(26-07-29) — '판매가' 칸이 늘어 7 열. */}
                <td colSpan={7} style={empty} className="kuun-caption">
                  라인 없음
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section style={panel}>
        <header style={panelHeader}>
          <span>가격 책정</span>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {data.pricings?.length ?? 0}건
          </span>
        </header>
        <div style={panelBody}>
          {/* 정정7(26-07-29) — 판매 대금만 있는 계약(판매 딜에서 합성된 계약)에서
              '가격 정보 없음'과 판매 대금이 같이 뜨는 자기모순을 막는다.
              정정9(26-07-29) — 마법사 경로 판매 줄만 있는 계약도 같은 자기모순이었다:
              합계는 만들 수 없지만(판매가 칸이 비어 있다) 표에는 판매 금액이 보이는데
              이 카드가 '가격 정보 없음'이라고 말했다. 그 경우엔 아래 안내가 대신 뜬다. */}
          {(!data.pricings || data.pricings.length === 0) &&
            saleTotal === null &&
            saleLinesFromMonthlyColumn.length === 0 && (
              <div style={empty} className="kuun-caption">
                가격 정보 없음
              </div>
            )}
          {data.pricings?.map((p) => (
            <div key={p.id} style={pricingRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{SCHEME_LABEL[p.scheme]}</div>
                <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
                  유효일: {kstYmd(p.effectiveFrom) ?? '-'}
                  {p.paymentSchedule && ` · ${p.paymentSchedule}`}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                {/* §D-3 — masked for non-sales roles. The currency suffix and
                    label stay visible (non-sensitive); only the number is
                    redacted. */}
                {/* 계약 사실화 1단계 정정7(26-07-29) — 0 은 '없음'이라 아예 적지 않는다.
                    종전에는 판매 계약(월 합계가 0 으로 저장된다)을 열면 "월: 0 KRW" 가
                    떴다. 같은 값을 계약서 PDF 는 인쇄하지 않았으므로 이제 두 곳이 같은
                    판정을 쓴다(위 amountOrNone 주석에 근거). */}
                {amountOrNone(p.totalMonthly) && (
                  <div>
                    월: <MaskedPrice value={amountOrNone(p.totalMonthly)} /> {p.currency}
                  </div>
                )}
                {amountOrNone(p.totalOneTime) && (
                  <div>
                    일시: <MaskedPrice value={amountOrNone(p.totalOneTime)} /> {p.currency}
                  </div>
                )}
                {amountOrNone(p.deposit) && (
                  <div>
                    보증금: <MaskedPrice value={amountOrNone(p.deposit)} /> {p.currency}
                  </div>
                )}
                {p.exchangeRateSnapshot !== '1' && p.exchangeRateSnapshot !== '1.0' && (
                  <div style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
                    × {p.exchangeRateSnapshot}
                  </div>
                )}
              </div>
            </div>
          ))}
          {/* 계약 사실화 1단계 정정7(26-07-29) — 판매 대금. 가격 책정 기록(ContractPricing)
              에는 월 금액 칸만 있어 판매 계약의 대금이 이 카드에 나올 자리가 없었다.
              판매 대금은 계약 줄에 있으므로 줄에서 낸 합계를 한 줄로 덧붙인다(셈법 근거는
              위 saleTotal 주석 — 사업 대시보드와 같은 셈법). */}
          {saleTotal !== null && (
            <div style={pricingRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>판매 대금</div>
                {/* 계약 사실화 1단계 정정8(26-07-29) — 종전 문구('판매 줄 N건')는 표에
                    보이는 판매 줄 수와 어긋났다. 이 N 은 **판매가 칸이 채워진 줄**의 수라서,
                    금액이 월 칸에 들어간 판매 줄(계약 등록 마법사 경로)은 빠진다 — 판매
                    2줄 중 1줄만 판매가를 가진 계약에서 표에는 2줄이 보이는데 캡션은 '1건'
                    이라 적었다. 합계 자체는 사업 대시보드 셈법과 같아 옳으므로 **문구만**
                    사실대로 고친다(합산 대상을 문구가 스스로 밝힌다). */}
                <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
                  판매가가 입력된 판매 줄 {saleLines.length}건의 판매가와 대수를 곱한 합계
                </div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                <div>
                  {/* §D-3 — 금액은 가격 권한 역할만. */}
                  판매: <MaskedPrice value={saleTotal} /> {saleCurrencies[0]}
                </div>
              </div>
            </div>
          )}
          {/* 계약 사실화 1단계 정정9(26-07-29) — 합계에서 빠진 판매 줄을 밝힌다.
              표의 판매가 칸에는 마법사 경로 줄의 금액도 (판매가로) 보이는데 위 합계에는
              들어가지 않는다 — 합계 셈법을 사업 대시보드와 같게 유지하기 때문이다
              (대시보드는 판매가 칸만 센다). 밝히지 않으면 운영자가 표와 합계를 대조하다
              어긋난 이유를 알 수 없다. 합계 줄이 아예 없는 계약(전 줄이 마법사 경로)에서도
              떠야 하므로 그 줄 안이 아니라 **카드의 독립된 줄**로 둔다.
              ⚠ 가격 권한 없는 역할에는 saleLinesFromMonthlyColumn 가 빈 배열이라 뜨지
              않는다(건수 자체가 "금액이 있다"는 신호다 — 마스킹이 앞). */}
          {saleLinesFromMonthlyColumn.length > 0 && (
            <div style={pricingRow}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, color: 'var(--fg-warning)' }}>
                  판매가 칸이 빈 판매 줄 {saleLinesFromMonthlyColumn.length}건
                </div>
                <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
                  금액이 월 단가 칸에 입력돼 있어 판매 대금 합계에 넣지 않았습니다. 표에는 판매가로
                  보이고 계약서 PDF에도 판매가로 인쇄됩니다.
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <ContractTerminateModal
        open={terminateOpen}
        onClose={() => setTerminateOpen(false)}
        contractId={data.id}
        contractHumanId={data.humanId}
      />

      {canBeEdited && (
        <ContractEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          contractId={data.id}
          contractHumanId={data.humanId}
          initial={{
            startDate: data.startDate,
            endDate: data.endDate,
            durationMonths: data.durationMonths,
            renewalScheduledAt: data.renewalScheduledAt ?? null,
          }}
        />
      )}

      {/* §E-3 — Infrastructure order modal. infraQ may not have resolved
          yet (initial=null is fine; the modal seeds an empty draft). */}
      {data.status !== ContractStatus.DRAFT && (
        <InfrastructureOrderModal
          open={infraOpen}
          onClose={() => setInfraOpen(false)}
          contractId={data.id}
          contractHumanId={data.humanId}
          initial={infraQ.data ?? null}
        />
      )}
    </div>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
        {label}
      </span>
      <span style={{ fontSize: 13 }}>{children}</span>
    </div>
  );
}

// (formatKRW removed — all price display sites switched to <MaskedPrice />
// which centralises formatting + role-based masking. See plan §D-3.)

const actionRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: 12,
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
};

const panel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};

const panelHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
  fontWeight: 600,
};

const panelBody: CSSProperties = {
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const detailGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16,
  padding: 16,
};

const lineTable: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };

const pricingRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '10px 12px',
  borderRadius: 'var(--r-input)',
};

const empty: CSSProperties = {
  padding: 16,
  textAlign: 'center',
};
